import { useEffect, useState } from 'react';

/**
 * Measures the width of a container element and keeps it in sync with viewport
 * resizes via ResizeObserver (with a window resize fallback).
 *
 * @param {React.RefObject} containerRef - ref to the DOM element to measure
 * @param {Object}  options
 * @param {number}  options.padding  - horizontal padding to subtract (default 32)
 * @param {number}  options.minWidth - minimum returned width (default 320)
 * @param {boolean} options.enabled  - skip measurement when false (e.g. SSR)
 * @returns {number} measured container width (minus padding), clamped to minWidth
 */
const useContainerWidth = (
  containerRef,
  { padding = 32, minWidth = 320, enabled = true } = {},
) => {
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;

    const measure = () => {
      const next = Math.max(minWidth, el.clientWidth - padding);
      setContainerWidth(next);
    };

    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [containerRef, padding, minWidth, enabled]);

  return containerWidth;
};

export default useContainerWidth;
